import React, { ReactNode, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Icon } from '../Icon';
import { TooltipIcon, TooltipWrapper } from '../Tooltip';
import { Label } from '../Typography';

export const List = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  list-style: none;
`;

interface IListItemProps {
  label: string;
  accordion?: boolean;
  accordionOpen?: boolean;
  children?: ReactNode;
  className?: string;
  isLoading?: boolean;
  tooltip?: string;
  value?: string | ReactNode;
}

const HEIGHT_DEFAULT = 66;

const LayoutStyles = css`
  display: flex;
  width: 100%;
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  overflow: hidden;
`;

const TextStyles = css`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  color: ${theme.color.neutral[800]};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(16.5)};
  }
`;

const Layout = styled.li`
  ${LayoutStyles}
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  height: ${toRem(HEIGHT_DEFAULT)};
  ${TextStyles}
  font-weight: ${theme.font.weight.medium};
  em {
    font-style: italic;
  }
`;

const AccordionLayout = styled.li`
  ${LayoutStyles}
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const AccordionHeader = styled.header`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${toRem(HEIGHT_DEFAULT)};
`;

interface IAccordionProps {
  isOpen?: boolean;
}

const AccordionContent = styled(motion.section)`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
`;

const AccordionChildren = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding-bottom: ${toRem(11)};
  ${TextStyles}
  font-weight: ${theme.font.weight.regular};
`;

const ToggleButton = styled.button<IAccordionProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${toRem(42)};
  height: ${toRem(42)};
  padding: 0 ${toRem(11)};
  border-radius: 50%;
  background-color: transparent;
  transform: rotateZ(0deg);
  transition:
    background-color ${theme.transition.button.default},
    transform ${theme.transition.button.default};

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotateZ(180deg);
    `}

  svg {
    position: relative;
    top: ${toRem(1)};
    width: ${toRem(20)};
    height: ${toRem(20)};
    fill: ${theme.color.neutral[900]};
  }

  &:hover {
    background-color: ${theme.color.neutral[100]};
  }
`;

const StyledTooltipWrapper = styled(TooltipWrapper)`
  svg {
    position: relative;
    top: ${toRem(-1)};
  }
`;

const LoaderLabelWidth = 77;

export const ListItem = ({ label, accordion, accordionOpen, children, className, isLoading, tooltip, value, ...rest }: IListItemProps) => {
  // UI State
  const [open, setOpen] = useState<boolean>(accordionOpen || false);

  // Label +/- Tooltip
  const renderLabel = tooltip ? (
    <StyledTooltipWrapper>
      <Label>{isLoading ? <Skeleton width={LoaderLabelWidth} /> : label}</Label>
      {isLoading ? null : (
        <TooltipIcon id={`tooltip-${label}`} place="right" iconMarginLeft={5.5}>
          {tooltip}
        </TooltipIcon>
      )}
    </StyledTooltipWrapper>
  ) : (
    <Label>{isLoading ? <Skeleton width={LoaderLabelWidth} /> : label}</Label>
  );

  return accordion ? (
    <AccordionLayout {...rest}>
      <AccordionHeader>
        {renderLabel}
        {isLoading ? null : (
          <ToggleButton aria-label={`Click to ${open ? 'minimize' : 'expand'}.`} isOpen={open} onClick={() => setOpen(!open)}>
            <Icon icon="Dropdown" />
          </ToggleButton>
        )}
      </AccordionHeader>

      <AccordionContent
        initial={open}
        animate={{
          height: open ? 'auto' : 0,
        }}
        transition={theme.transition.spring.accordion}
      >
        <AccordionChildren>{children}</AccordionChildren>
      </AccordionContent>
    </AccordionLayout>
  ) : (
    <Layout className={className} {...rest}>
      {renderLabel}
      {isLoading ? <Skeleton width={132} /> : value || null}
    </Layout>
  );
};
